import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ContractChangeStatusEnum } from 'gql/graphql';
import { PriceListTypeEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { ExportCSVReport } from 'utils/createCSVReportFile';
import { downloadURI } from 'utils/downloadDataURI';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { exportContractChangeAnalyticsAsExcelGql } from '../AgreementsQueries';

export const useUploadAgreement = ({
    currentAgreementId,
    currentAgreementStatus,
}: {
    currentAgreementId?: number;
    currentAgreementStatus?: ContractChangeStatusEnum;
}) => {
    const dispatch = useDispatch();
    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

    const [
        exportContractData,
        exportContractStatus,
        exportContractError,
        exportContractRequest,
        setExportContractData,
        abortRequest,
    ] = useRequest({
        requestId: 'exportContractChangeAnalyticsAsExcel',
        query: exportContractChangeAnalyticsAsExcelGql,
        withEffect: false,
    });

    const variables = useMemo(
        () => ({
            contractChangeId: currentAgreementId,
            contractChangeToCompareWithId: null,
            priceListType: [
                PriceListTypeEnum.Uer,
                PriceListTypeEnum.Mtr,
                PriceListTypeEnum.Ratio,
                PriceListTypeEnum.Others,
            ],
        }),
        [currentAgreementId],
    );

    const handleUploadAgreement = useCallback(
        (agreementId: number | undefined, agreementStatus: ContractChangeStatusEnum | undefined) => {
            setIsSubmitted(true);
            dispatch(
                exportContractRequest(
                    currentAgreementId
                        ? variables
                        : {
                              contractChangeId: agreementId,
                              contractChangeToCompareWithId: null,
                              priceListType: [
                                  PriceListTypeEnum.Uer,
                                  PriceListTypeEnum.Mtr,
                                  PriceListTypeEnum.Ratio,
                                  PriceListTypeEnum.Others,
                              ],
                          },
                ),
            );

            sendGpnAnalyticsEvent({
                componentId: 'rim_upload DS button_pressed',
                projectId,
                contractId: Number(projectData?.contractId),
                agreementId: agreementId || currentAgreementId,
                agreementStatus: agreementStatus || currentAgreementStatus,
                projectName: projectData?.name,
                projectType: projectData?.contractType,
                projectDO: projectData?.subsidiary?.name,
                methodology: projectData?.methodology,
            });
        },
        [
            currentAgreementId,
            currentAgreementStatus,
            dispatch,
            exportContractRequest,
            projectData?.contractId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            sendGpnAnalyticsEvent,
            variables,
        ],
    );

    const handleCloseUploadAgreementError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const handleCreateReportUploadAgreement = useCallback(() => {
        ExportCSVReport(exportContractData?.uerExportContractChangeAnalyticsAsExcel?.validation);
    }, [exportContractData?.uerExportContractChangeAnalyticsAsExcel?.validation]);

    useEffect(() => {
        if (
            exportContractStatus === RequestStatus.SUCCESS &&
            exportContractData?.uerExportContractChangeAnalyticsAsExcel?.data
        ) {
            const { dataUrl, name } = exportContractData.uerExportContractChangeAnalyticsAsExcel.data;

            downloadURI(dataUrl, name);
            dispatch(setExportContractData(undefined));
            setIsSubmitted(false);
        }
    }, [
        dispatch,
        exportContractData?.uerExportContractChangeAnalyticsAsExcel?.data,
        exportContractStatus,
        setExportContractData,
    ]);

    const cancelCopyRequest = useCallback(() => {
        dispatch(abortRequest());
    }, [abortRequest, dispatch]);

    useEffect(
        () => () => {
            cancelCopyRequest();
        },
        [cancelCopyRequest],
    );

    const isCopying = useMemo(() => exportContractStatus === RequestStatus.FETCHING, [exportContractStatus]);

    return {
        isSubmitted,
        exportContractData: exportContractData?.uerExportContractChangeAnalyticsAsExcel,
        exportContractStatus,
        exportContractError,
        handleUploadAgreement,
        handleCloseUploadAgreementError,
        handleCreateReportUploadAgreement,
        cancelCopyRequest,
        isCopying,
    };
};
